/*
 * The bearer-token store: the single owner of the JWT in the browser.
 *
 * Deliberately framework-free so both the imperative API client (header attach
 * + 401 handling) and the React auth context can read/write it without a
 * circular import. Persists to localStorage so a reload keeps the session, and
 * publishes changes (storage events + in-tab subscribers) so the auth context
 * stays in sync — including a logout in another tab.
 *
 * Tokens are brand-scoped after login/switch-brand, or principal-only after
 * register; the store does not interpret the token, it only holds the string.
 */

const STORAGE_KEY = "admanage.auth.token";

type Listener = (token: string | null) => void;

let current: string | null = readInitial();
const listeners = new Set<Listener>();

function readInitial(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function emit(): void {
  for (const fn of listeners) fn(current);
}

export function getToken(): string | null {
  return current;
}

export function setToken(token: string | null): void {
  current = token;
  try {
    if (token) window.localStorage.setItem(STORAGE_KEY, token);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode / quota) — keep the in-memory value.
  }
  emit();
}

export function clearToken(): void {
  setToken(null);
}

/** Subscribe to token changes; returns an unsubscribe fn. */
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Cross-tab sync: another tab logging in/out updates this tab's view.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      current = e.newValue;
      emit();
    }
  });
}
